import { useNavigate } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import axios from 'axios';
import { useAppDispatch, useAppSelector } from '@/store';
import { authActions, authSelector } from '@/store/reducers/auth';
import userThunks from '@/store/thunks/user';
import { useTranslation } from 'react-i18next';
import UserUtils from '@/utils/user';
import { cognitoService } from '@/services/cognito';
import { CognitoSession } from '@/types/user';
import commonUtils from '@/utils/common';
import { unwrapThunkAxiosError } from '@/utils/forcedSessionLogout';
import { isWafBlockedError } from '@/utils/wafBlockedError';
import { resolveLevelTestGate } from '@/utils/levelTestGate';

const UseCheckAuth = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { loggedIn, user, appLoaded, interfaceLanguage, loggedUserId, placementTestCompleted } =
    useAppSelector(authSelector);
  const { i18n } = useTranslation();
  const checkAuthTimer = useRef<any>();

  const fetchLevelsAndLanguages = async () => {
    await Promise.all([
      dispatch(userThunks.fetchLevels()).unwrap(),
      dispatch(userThunks.fetchLanguages()).unwrap(),
    ]);
  };

  const getFragmentParams = (): Record<string, string> => {
    if (!window.location.hash) return {};

    return window.location.hash
      .substring(1)
      .split('&')
      .reduce(
        (params, pair) => {
          const [key, value] = pair.split('=');
          if (key && value) {
            params[key] = decodeURIComponent(value);
          }
          return params;
        },
        {} as Record<string, string>
      );
  };

  const checkAuthStatus = async () => {
    // If the loggedIn state is set to false, it means the localStorage has no item called 'loggedIn'
    // We are going to use cookies in the future, so that will be our only indicator for whether the user is logged in or not
    if (!localStorage.getItem('token')) {
      dispatch(authActions.appLoaded());

      const currentQuery = window.location.search;
      const queryParams = new URLSearchParams(currentQuery);

      const language = queryParams.get('language') || '';
      if (language) {
        dispatch(authActions.setNativeLanguage(language));
        dispatch(authActions.setInterfaceLanguage(language));
      }

      const isRegistrationPage = window.location.pathname.startsWith('/registration');
      const isDealErrorMessagePage = window.location.pathname.startsWith('/deal-error-message');
      const isWafBlockedPage = window.location.pathname.startsWith('/waf-blocked');

      if (isRegistrationPage || isDealErrorMessagePage || isWafBlockedPage) {
        return;
      }

      const code = queryParams.get('code');
      if (code) {
        return navigate('/login?code=' + code, { replace: true });
      }
      const params = getFragmentParams();
      if (params.access_token) {
        return navigate('/login?access_token=' + params.access_token, { replace: true });
      }

      return navigate(`/login`, { replace: true });
    }

    // Since the cookie is going to be HTTP only which means accessed only on backend,
    // We will an authentication request and if the server verifies the user, they will remain logged in

    try {
      const user = await dispatch(userThunks.userInfo({ retryCount: 1, trySignup: true })).unwrap();
      const isTeacher = UserUtils.isTeacher(user?.data);
      const isAdmin = UserUtils.isAdmin(user?.data);

      const isClassCodeStudent = user?.data?.metadata?.username ? true : false;

      const cognitoSession = (await cognitoService.checkCognitoSession(
        isClassCodeStudent
      )) as CognitoSession;
      if (cognitoSession && isTeacher) {
        localStorage.setItem('teacher-level', cognitoSession.teacherLevel.toString());
      }

      await fetchLevelsAndLanguages();

      dispatch(authActions.appLoaded());
    } catch (error) {
      const axiosLike = unwrapThunkAxiosError(error);
      if (isWafBlockedError(axiosLike)) {
        window.location.assign(new URL('/waf-blocked', window.location.origin).toString());
        return;
      }
      const status = axios.isAxiosError(axiosLike) ? axiosLike.response?.status : undefined;
      if (status === 401 || status === 403) {
        return;
      }
      console.error('Error fetching data:', error);
      commonUtils.showGlobalError({ i18nKey: 'error.somethingWentWrong', silent: true });
    }
  };

  useEffect(() => {
    if (interfaceLanguage) {
      i18n.changeLanguage(interfaceLanguage);
      const direction = ['he', 'ar'].includes(interfaceLanguage) ? 'rtl' : 'ltr';
      document.querySelector('html')?.setAttribute('dir', direction);

      const interdeal = (window as any).interdeal;
      if (interdeal) {
        interdeal.Menulang = interfaceLanguage;
        if (interdeal.SetLocale) {
          try {
            interdeal.SetLocale(interfaceLanguage);
          } catch (error) {}
        }
      }
    }
  }, [interfaceLanguage]);

  useEffect(() => {
    const pathname = window.location.pathname;
    const pathAfterDashboard = pathname.replace(/^\/dashboard\/?/, '').trim();
    const firstDashboardSegment = pathAfterDashboard ? pathAfterDashboard.split('/')[0] : '';

    const isTeacher = UserUtils.isTeacher(user);
    const isAdmin = UserUtils.isAdmin(user);

    if (appLoaded && loggedIn) {
      if (isAdmin) {
        return navigate('/onboarding', { replace: true });
      } else if (isTeacher) {
        if (user?.metadata?.role !== 'teacher') {
          return navigate('/dashboard/my-classes', { replace: true });
        }
        // Allow teachers to stay on valid dashboard routes (use pathname so query params don't break the check)
        const teacherAllowedRoutes = [
          'games',
          'my-students',
          'my-classes',
          'profile',
          'my-tutor',
          'my-tutor-chat',
          'level-test',
          'skill',
          'quiz',
        ];
        if (firstDashboardSegment && teacherAllowedRoutes.includes(firstDashboardSegment)) {
          return;
        }
        return navigate('/dashboard/my-classes', { replace: true });
      }

      // ---------------------------------------------------------------------------
      // TEMPORARY (screenshot / manual QA only) - DO NOT COMMIT.
      // Lets you open /dashboard/no-level-assigned directly. Without this the gate
      // resolves a normal account to 'dashboard' and redirects to /dashboard/games.
      // Delete this whole block to restore real behavior (a student WITH a level must
      // not be able to sit on this route).
      if (firstDashboardSegment === 'no-level-assigned') {
        return;
      }
      // ---------------------------------------------------------------------------

      const groupSettings = user?.metadata?.organizationDetails?.groupSettings;
      const decision = resolveLevelTestGate({
        hasLevel: user?.progress?.userLevel !== undefined,
        placementTestRequired: groupSettings?.placementTestRequired,
        defaultStartingLevel: groupSettings?.defaultStartingLevel,
        placementTestCompleted,
        hasProgress: (user?.progress?.completedActivities ?? 0) > 0,
      });

      if (decision.route === 'blocked') {
        if (firstDashboardSegment === 'no-level-assigned') {
          return;
        }
        return navigate('/dashboard/no-level-assigned', { replace: true });
      }

      if (decision.route === 'dashboard') {
        if (decision.presetLevel !== undefined) {
          dispatch(authActions.presetBypassedLevel({ userLevel: decision.presetLevel }));
        }

        if (firstDashboardSegment === 'level-test') {
          return;
        }
        return navigate('/dashboard/games', { replace: true });
      }

      return navigate('/dashboard/level-test', { replace: true });
    }
  }, [appLoaded, loggedIn, placementTestCompleted]);

  useEffect(() => {
    const clearCheckAuthTimer = () => {
      if (checkAuthTimer.current) {
        clearTimeout(checkAuthTimer.current);
        checkAuthTimer.current = null;
      }
    };

    const checkAuth = () => {
      clearCheckAuthTimer();
      checkAuthStatus();
    };

    let checkLoggedUserIdTimer: any = null;

    const clearCheckLoggedUserIdTimer = () => {
      if (checkLoggedUserIdTimer) {
        clearInterval(checkLoggedUserIdTimer);
        checkLoggedUserIdTimer = null;
      }
    };

    const checkLoggedUserId = () => {
      if (!checkLoggedUserIdTimer) {
        return;
      }

      const localStorageLoggedUserId = localStorage.getItem('loggedUserId') || '';
      const storeLoggedUserId = loggedUserId || '';

      if (localStorageLoggedUserId !== storeLoggedUserId && !localStorageLoggedUserId) {
        dispatch(authActions.setLoggedUserId(localStorageLoggedUserId));
      }
    };

    checkAuthTimer.current = setTimeout(checkAuth, 100);
    checkLoggedUserIdTimer = setInterval(checkLoggedUserId, 1000);

    return () => {
      clearCheckAuthTimer();
      clearCheckLoggedUserIdTimer();
    };
  }, [loggedUserId]);
};

export default UseCheckAuth;
